import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { createStructuredSelector } from 'reselect'
import { subscriptionPlansDispatchers as dispatchSelector } from 'reduxModules/subscriptions/dispatchers'
import { LIFETIME_PLAN_ID } from 'reduxModules/subscriptions'
import {
  globalPromoSelector,
  isGlobalPromoEnabledSelector
} from 'reduxModules/subscriptions/selectors'

import './styles.css'

// selectPlan recognizes plan.name === 'Lifetime' and routes through
// seedLifetimeFromPlanList → activateSubscribingMode → /checkout. The real
// backend plan id is hardcoded here (see LIFETIME_PLAN_ID) so the request body
// is correct even if state.subscriptions.plans hasn't loaded yet.
const LIFETIME_PLAN = {
  id: LIFETIME_PLAN_ID,
  name: 'Lifetime',
  price: 1199,
  rank: 5
}

const FEATURES = [
  'Unlimited Lifetime Access',
  'All future courses included',
  'Exclusive member perks',
  'Interest-free installment payments available'
]

const formatLifetimePrice = (amount) => {
  const n = parseFloat(amount)
  if (!Number.isFinite(n)) return null
  const hasFraction = n % 1 !== 0
  return n.toLocaleString('en-US', {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2
  })
}

export function LifetimePromo({
  selectPlan,
  globalPromo,
  isGlobalPromoEnabled
}) {
  const lifetimeEntry =
    isGlobalPromoEnabled && globalPromo?.plans
      ? globalPromo.plans[LIFETIME_PLAN_ID]
      : null
  const hasDiscount =
    lifetimeEntry && parseFloat(lifetimeEntry.discountAmount) > 0
  const discountedPrice = hasDiscount
    ? formatLifetimePrice(lifetimeEntry.priceDiscount)
    : null
  const ctaLabel = hasDiscount
    ? `Get Lifetime Access & save ${globalPromo.discount}${
        globalPromo.discountType === 'pctg' ? '%' : ''
      }`
    : 'Get Lifetime Access'

  return (
    <div className="LifetimePromo smaller-wrapper">
      <section aria-labelledby="lifetime-promo-title">
        <h2 className="title-division">Lifetime Access</h2>
        <div className="lifetime-promo__frame">
          <div className="lifetime-promo__badge">Best Long-Term Value</div>

          <div className="lifetime-star lifetime-promo__star" aria-hidden="true">
            ★
          </div>

          <h2 id="lifetime-promo-title" className="lifetime-promo__title">
            Lifetime Access
          </h2>

          {discountedPrice ? (
            <>
              <div className="lifetime-promo__price">${discountedPrice}</div>
              <div className="lifetime-promo__price-before">$1,199</div>
            </>
          ) : (
            <div className="lifetime-promo__price">$1,199</div>
          )}
          <div className="lifetime-promo__one-time">One-time payment</div>
          {hasDiscount && globalPromo.codename && (
            <div className="lifetime-promo__coupon">
              Coupon{' '}
              <span className="lifetime-promo__coupon-code">
                {globalPromo.codename}
              </span>{' '}
              applied
            </div>
          )}

          <p className="lifetime-promo__equivalent">
            Equivalent to ~3 years of Yearly
          </p>

          <ul className="lifetime-promo__features">
            {FEATURES.map((feature) => (
              <li key={feature} className="lifetime-promo__feature">
                <span aria-hidden="true" className="lifetime-promo__check">
                  ✓
                </span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <p className="lifetime-promo__save">
            Save vs 3+ years of subscription
          </p>

          <button
            type="button"
            className="lifetime-promo__cta"
            onClick={() => selectPlan(LIFETIME_PLAN)}
          >
            {ctaLabel}
          </button>

          <p className="lifetime-promo__intro">
            Intro pricing — may increase in the future
          </p>
        </div>
      </section>
    </div>
  )
}

LifetimePromo.propTypes = {
  selectPlan: PropTypes.func.isRequired,
  globalPromo: PropTypes.shape({
    codename: PropTypes.string,
    discount: PropTypes.number,
    discountType: PropTypes.string,
    plans: PropTypes.object
  }),
  isGlobalPromoEnabled: PropTypes.bool
}

LifetimePromo.defaultProps = {
  globalPromo: { plans: {} },
  isGlobalPromoEnabled: false
}

const stateSelector = createStructuredSelector({
  globalPromo: globalPromoSelector,
  isGlobalPromoEnabled: isGlobalPromoEnabledSelector
})

export default connect(stateSelector, dispatchSelector)(LifetimePromo)
